(() => {
  'use strict';
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const ease = value => 1 - Math.pow(1 - clamp(value), 3);
  const damp = (current, target, milliseconds) => current + (target - current) * (1 - Math.exp(-Math.max(0, milliseconds) / 55));
  const revealDuration = count => 520 + Math.min(Math.max(0, count - 1) * 70, 210);
  const revealFor = (progress, index, count) => ease((clamp(progress) * revealDuration(count) - Math.min(index * 70, 210)) / 520);
  const frameFor = (progress, narrow, pointerX = 0, pointerY = 0, cover = 0) => {
    const p = clamp(progress) - .5;
    return {
      photoY: p * (narrow ? -16 : -30) - pointerY * 2,
      photoX: pointerX * -3,
      photoTurn: p * (narrow ? 1.2 : 2) + pointerX * .12,
      frontY: p * (narrow ? 24 : 44) + pointerY * 3,
      frontX: pointerX * 6,
      scale: 1 - clamp(cover) * .025
    };
  };
  const canStack = (height, viewport, header) => height <= viewport - header - 40;
  if (typeof window === 'undefined' && typeof module === 'object' && module.exports) {
    module.exports = { clamp, damp, frameFor, revealFor, revealDuration, canStack };
    return;
  }
  if (window.OHHomeMotion) return;

  const sections = new Map(), targets = new Map(), headings = new Map(), active = new Set(), exiting = new Set();
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  let paused = document.documentElement.dataset.ohMotionPaused === 'true';
  let raf = 0, lastTime = null, layoutDirty = true, splitDirty = true, splitBatch = null;
  let inputIntent = 'pointer', focusRequest = null, destroyed = false;
  let fontsReady = !document.fonts || document.fonts.status !== 'loading';
  let viewport = { width: 0, height: 0, header: 76 };
  const format = (value, unit) => value.toFixed(3) + unit;
  const timeNow = () => performance.now();
  const observe = element => observer ? observer.observe(element) : active.add(element);
  const setData = (element, key, value) => { if (element.dataset[key] !== value) element.dataset[key] = value; };
  const toggleClass = (element, key, value) => {
    if (element.classList.contains(key) === value) return false;
    element.classList.toggle(key, value);
    return true;
  };
  const writeStyle = (record, name, value) => {
    if (record.written[name] === value) return;
    record.element.style.setProperty(name, value);
    record.written[name] = value;
  };

  function schedule() {
    const shouldAnimate = !paused && !document.hidden && !destroyed && active.size;
    if (!raf && !document.hidden && !destroyed && (shouldAnimate || layoutDirty || splitBatch || splitDirty && fontsReady || exiting.size || focusRequest)) {
      if (lastTime === null) lastTime = timeNow();
      raf = requestAnimationFrame(paint);
    }
  }

  function readViewport() {
    const width = innerWidth, height = innerHeight;
    if (width !== viewport.width) splitDirty = true;
    if (layoutDirty || width !== viewport.width || height !== viewport.height) {
      viewport = { width, height, header: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--oh-header-height')) || 76 };
      layoutDirty = true;
    }
  }

  function prepareSplits() {
    const batch = [];
    headings.forEach(record => {
      if (!record.element.isConnected || record.width === viewport.width && record.ready) return;
      const fragment = document.createDocumentFragment();
      const words = record.text.split(' ');
      const measures = words.map((word, index) => {
        const span = document.createElement('span');
        span.textContent = word; span.style.display = 'inline-block';
        fragment.append(span);
        if (index < words.length - 1) fragment.append(document.createTextNode(' '));
        return span;
      });
      batch.push({ record, words, measures, fragment });
    });
    splitDirty = false;
    if (!batch.length) return false;
    batch.forEach(item => item.record.element.replaceChildren(item.fragment));
    splitBatch = batch;
    layoutDirty = true;
    schedule();
    return true;
  }

  function measureSplits(rect) {
    if (!splitBatch) return [];
    return splitBatch.map(item => {
      const groups = [];
      item.measures.forEach((word, index) => {
        const top = Math.round(word.offsetTop);
        if (!groups.length || Math.abs(groups[groups.length - 1].top - top) > 2) groups.push({ top, words: [] });
        groups[groups.length - 1].words.push(item.words[index]);
      });
      return { ...item, groups, box: rect(item.record.element) };
    });
  }

  function commitSplits(plans) {
    plans.forEach(({ record, groups, box }) => {
      const fragment = document.createDocumentFragment(), lines = [];
      groups.forEach((group, index) => {
        const mask = document.createElement('span'), inner = document.createElement('span');
        mask.className = 'oh-motion-line'; inner.className = 'oh-motion-line__inner';
        mask.setAttribute('aria-hidden', 'true');
        Object.assign(mask.style, { display: 'block', overflow: 'clip', paddingBlock: '.13em', marginBlock: '-.13em' });
        inner.style.display = 'block';
        inner.textContent = group.words.join(' ');
        mask.append(inner); fragment.append(mask);
        if (index < groups.length - 1) fragment.append(document.createTextNode(' '));
        lines.push(inner);
      });
      if (!record.ready && box.top < viewport.height * .74) record.done = true;
      record.done = record.done || paused;
      record.lines = lines; record.lineValues = []; record.width = viewport.width; record.ready = true;
      record.element.replaceChildren(fragment);
      record.element.setAttribute('aria-label', record.text);
      if (!record.done) observe(record.element);
      writeHeading(record, record.done ? 1 : record.progress);
    });
    splitBatch = null;
  }

  function writeHeading(record, progress) {
    record.progress = progress;
    record.lines.forEach((line, index) => {
      const value = 'translateY(' + format((1 - revealFor(progress, index, record.lines.length)) * 112, '%') + ')';
      if (record.lineValues[index] !== value) { line.style.transform = value; record.lineValues[index] = value; }
    });
    const complete = progress >= 1;
    setData(record.element, 'ohReveal', complete ? 'complete' : record.startedAt === null ? 'ready' : 'revealing');
    if (complete) {
      record.done = true;
      observer?.unobserve(record.element);
      active.delete(record.element);
    }
  }

  function stepHeading(record, box, now) {
    if (!record.ready || record.done) return false;
    if (paused || box.bottom <= 0) { writeHeading(record, 1); return false; }
    if (record.startedAt === null && box.top <= viewport.height * .9) record.startedAt = now;
    if (record.startedAt === null) return false;
    writeHeading(record, clamp((now - record.startedAt) / revealDuration(record.lines.length)));
    return !record.done;
  }

  function mountTarget(element, kind) {
    if (targets.has(element)) return;
    const record = { element, kind, x: 0, y: 0, tx: 0, ty: 0, pointer: null, pointerDirty: false, progress: null, cover: null, height: 0, snap: true, written: {}, listeners: [] };
    const on = (type, listener) => { element.addEventListener(type, listener, { passive: true }); record.listeners.push([type, listener]); };
    on('pointermove', event => {
      if (paused || !fine.matches || event.pointerType === 'touch') return;
      record.pointer = { x: event.clientX, y: event.clientY };
      record.pointerDirty = true;
      schedule();
    });
    on('pointerleave', () => { record.pointer = null; record.pointerDirty = false; record.tx = record.ty = 0; schedule(); });
    targets.set(element, record);
    observe(element);
  }

  function measure() {
    const rects = new Map();
    const rect = element => {
      if (!rects.has(element)) rects.set(element, element.getBoundingClientRect());
      return rects.get(element);
    };
    const splits = measureSplits(rect), sectionPlans = [], motionPlans = [], headingPlans = [];
    if (layoutDirty) {
      sections.forEach(record => {
        const heights = record.cards.map(card => card.offsetHeight);
        record.cards.forEach((card, index) => { const target = targets.get(card); if (target) target.height = heights[index]; });
        sectionPlans.push({ record, fits: heights.length > 1 && heights.every(height => canStack(height, viewport.height, viewport.header)) });
      });
    }
    active.forEach(element => {
      const heading = headings.get(element);
      if (heading && !heading.done && heading.ready) headingPlans.push({ record: heading, box: rect(element) });
      const record = targets.get(element);
      if (!record || !element.isConnected) return;
      const plan = { record };
      if (record.pointerDirty) plan.pointerBox = rect(element);
      if (record.kind === 'stack') {
        const marker = element.previousElementSibling;
        const top = marker?.matches('[data-oh-stack-marker]') ? rect(marker).top : rect(element).top;
        const height = record.height || element.offsetHeight;
        const next = element.nextElementSibling;
        plan.progress = clamp((viewport.height - top) / Math.max(1, viewport.height + height));
        plan.cover = next?.matches('[data-oh-stack-marker]') ? clamp((viewport.header + height - rect(next).top) / Math.max(1, height)) : 0;
      }
      motionPlans.push(plan);
    });
    const focus = focusRequest?.element.isConnected ? { ...focusRequest, box: rect(focusRequest.element) } : null;
    return { splits, sectionPlans, motionPlans, headingPlans, focus };
  }

  function applySections(plans) {
    let changed = false;
    plans.forEach(({ record, fits }) => {
      record.fits = fits;
      const stack = fits && !record.keyboardHeld;
      changed = toggleClass(record.element, 'can-stack', stack) || changed;
      setData(record.element, 'ohStackMode', paused ? 'paused' : record.keyboardHeld ? 'keyboard-flow' : fits ? 'sticky' : 'flow');
    });
    return changed;
  }

  function resetPointer(record) {
    record.x = record.y = record.tx = record.ty = 0;
    record.pointer = null; record.pointerDirty = false; record.snap = true;
    writeTarget(record);
  }

  function writeTarget(record) {
    if (record.kind === 'room') {
      writeStyle(record, '--oh-pointer-rx', format(-record.y * 1.5, 'deg'));
      writeStyle(record, '--oh-pointer-ry', format(record.x * 2, 'deg'));
      writeStyle(record, '--oh-photo-x', format(record.x * -3, 'px'));
      writeStyle(record, '--oh-photo-y', format(record.y * -3, 'px'));
      setData(record.element, 'ohMotionState', [record.x.toFixed(2), record.y.toFixed(2)].join(','));
      return;
    }
    const frame = frameFor(record.progress ?? .5, viewport.width <= 900, record.x, record.y, record.cover ?? 0);
    writeStyle(record, '--oh-photo-x', format(frame.photoX, 'px'));
    writeStyle(record, '--oh-photo-y', format(frame.photoY, 'px'));
    writeStyle(record, '--oh-photo-turn', format(frame.photoTurn, 'deg'));
    writeStyle(record, '--oh-front-x', format(frame.frontX, 'px'));
    writeStyle(record, '--oh-front-y', format(frame.frontY, 'px'));
    writeStyle(record, '--oh-stack-scale', frame.scale.toFixed(4));
    setData(record.element, 'ohMotionState', [frame.photoY.toFixed(1), frame.photoTurn.toFixed(2), frame.frontY.toFixed(1), frame.scale.toFixed(3)].join(','));
  }

  function stepTarget(plan, milliseconds) {
    const record = plan.record;
    if (record.pointerDirty && plan.pointerBox && record.pointer) {
      record.tx = clamp((record.pointer.x - plan.pointerBox.left) / Math.max(1, plan.pointerBox.width) * 2 - 1, -1, 1);
      record.ty = clamp((record.pointer.y - plan.pointerBox.top) / Math.max(1, plan.pointerBox.height) * 2 - 1, -1, 1);
      record.pointerDirty = false;
    }
    if (!fine.matches) record.tx = record.ty = 0;
    const approach = (value, goal, epsilon) => Math.abs(value - goal) <= epsilon ? goal : damp(value, goal, milliseconds);
    record.x = approach(record.x, record.tx, .002);
    record.y = approach(record.y, record.ty, .002);
    if (record.kind === 'stack') {
      record.progress = record.snap || record.progress === null ? plan.progress : approach(record.progress, plan.progress, .0015);
      record.cover = record.snap || record.cover === null ? plan.cover : approach(record.cover, plan.cover, .0015);
    }
    record.snap = false;
    writeTarget(record);
    return record.x !== record.tx || record.y !== record.ty || record.kind === 'stack' && (record.progress !== plan.progress || record.cover !== plan.cover);
  }

  function paint(timestamp) {
    raf = 0;
    if (destroyed || document.hidden) { lastTime = null; return; }
    const now = Number.isFinite(timestamp) ? timestamp : timeNow();
    const milliseconds = lastTime === null ? 1000 / 60 : clamp(now - lastTime, 0, 48);
    lastTime = now;
    readViewport();
    if (!splitBatch && splitDirty && fontsReady && prepareSplits()) return;
    const plans = measure();
    layoutDirty = false;
    if (splitBatch) commitSplits(plans.splits);
    sections.forEach(record => {
      toggleClass(record.element, 'is-home-motion', true);
      toggleClass(record.element, 'is-motion-paused', paused);
    });
    targets.forEach(record => toggleClass(record.element, 'is-home-motion-target', true));
    const stackChanged = applySections(plans.sectionPlans);
    let moving = false;
    exiting.forEach(element => {
      const target = targets.get(element);
      if (target) resetPointer(target);
      const heading = headings.get(element);
      if (heading?.startedAt !== null && heading?.ready && !heading.done) writeHeading(heading, 1);
    });
    exiting.clear();
    if (!paused) {
      plans.motionPlans.forEach(plan => { moving = stepTarget(plan, milliseconds) || moving; });
      plans.headingPlans.forEach(plan => { moving = stepHeading(plan.record, plan.box, now) || moving; });
    }
    if (plans.focus) {
      if (stackChanged && plans.focus.waitForLayout) focusRequest.waitForLayout = false;
      else {
        const box = plans.focus.box;
        if (box.top < viewport.header + 8 || box.bottom > viewport.height - 8) plans.focus.element.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        focusRequest = null;
      }
    } else focusRequest = null;
    if (stackChanged) layoutDirty = true;
    if (moving || layoutDirty || splitBatch || splitDirty && fontsReady || focusRequest) schedule();
    else lastTime = null;
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!targets.has(entry.target) && !headings.has(entry.target)) return;
      if (entry.isIntersecting) {
        if (headings.get(entry.target)?.done) return;
        active.add(entry.target);
        const record = targets.get(entry.target);
        if (record) record.snap = true;
      } else {
        active.delete(entry.target);
        exiting.add(entry.target);
      }
    });
    schedule();
  }, { rootMargin: '100px 0px' }) : null;

  function mount(scope = document) {
    scope.querySelectorAll('[data-oh-home-section]').forEach(element => {
      if (!sections.has(element)) sections.set(element, { element, cards: Array.from(element.querySelectorAll('[data-oh-stack-card]')), keyboardHeld: false, fits: false });
    });
    scope.querySelectorAll('[data-oh-pointer-room]').forEach(element => mountTarget(element, 'room'));
    scope.querySelectorAll('[data-oh-stack-card]').forEach(element => mountTarget(element, 'stack'));
    scope.querySelectorAll('[data-oh-motion-heading]').forEach(element => {
      if (headings.has(element)) return;
      const text = element.textContent.trim().replace(/\s+/g, ' ');
      if (!text) return;
      headings.set(element, { element, text, previousLabel: element.getAttribute('aria-label'), ready: false, width: null, lines: [], lineValues: [], progress: paused ? 1 : 0, done: paused, startedAt: null });
    });
    layoutDirty = true; splitDirty = true;
    schedule();
  }

  function setPaused(value) {
    const next = Boolean(value);
    if (next === paused) return;
    paused = next;
    if (paused) focusRequest = null;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    lastTime = null;
    sections.forEach(record => {
      toggleClass(record.element, 'is-motion-paused', paused);
      setData(record.element, 'ohStackMode', paused ? 'paused' : record.keyboardHeld ? 'keyboard-flow' : record.fits ? 'sticky' : 'flow');
    });
    if (paused) headings.forEach(record => { record.done = true; if (record.ready) writeHeading(record, 1); });
    layoutDirty = true;
    if (!paused || splitBatch) schedule();
  }

  function refresh() {
    if (destroyed) return;
    layoutDirty = true;
    if (innerWidth !== viewport.width) splitDirty = true;
    schedule();
  }

  function unmount(scope) {
    targets.forEach((record, element) => {
      if (!scope.contains(element)) return;
      record.listeners.forEach(([type, listener]) => element.removeEventListener(type, listener));
      observer?.unobserve(element); active.delete(element); exiting.delete(element); targets.delete(element);
    });
    headings.forEach((record, element) => {
      if (!scope.contains(element)) return;
      observer?.unobserve(element); active.delete(element); exiting.delete(element); headings.delete(element);
    });
    sections.forEach((record, element) => { if (scope.contains(element)) sections.delete(element); });
    if (focusRequest && scope.contains(focusRequest.element)) focusRequest = null;
    if (splitBatch) splitBatch = splitBatch.filter(item => !scope.contains(item.record.element));
  }

  window.OHHomeMotion = { setPaused, refresh, get paused() { return paused; } };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', refresh, { passive: true });
  document.addEventListener('visibilitychange', () => {
    lastTime = null;
    if (document.hidden) { if (raf) cancelAnimationFrame(raf); raf = 0; }
    else { layoutDirty = true; schedule(); }
  });
  document.addEventListener('oh:motion-change', event => { if (typeof event.detail?.paused === 'boolean') setPaused(event.detail.paused); });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  document.addEventListener('keydown', event => { if (!event.metaKey && !event.ctrlKey && !event.altKey) inputIntent = 'keyboard'; });
  document.addEventListener('pointerdown', () => { inputIntent = 'pointer'; focusRequest = null; }, { passive: true });
  document.addEventListener('focusin', event => {
    const card = event.target.closest?.('[data-oh-stack-card]');
    if (!card || paused || inputIntent !== 'keyboard') return;
    const section = card.closest('[data-oh-home-section]'), record = sections.get(section);
    if (!record) return;
    record.keyboardHeld = true;
    focusRequest = { element: event.target, waitForLayout: true };
    layoutDirty = true;
    schedule();
  });
  document.addEventListener('focusout', event => {
    if (focusRequest?.element === event.target) focusRequest = null;
    sections.forEach(record => {
      if (!record.keyboardHeld || !record.element.contains(event.target) || record.element.contains(event.relatedTarget)) return;
      record.keyboardHeld = false;
      layoutDirty = true;
    });
    schedule();
  });
  fine.addEventListener?.('change', () => {
    targets.forEach(record => { record.pointer = null; record.pointerDirty = false; record.tx = record.ty = 0; });
    schedule();
  });
  const start = () => {
    mount();
    if (!fontsReady) document.fonts.ready.then(() => { fontsReady = true; splitDirty = true; layoutDirty = true; schedule(); }).catch(() => { fontsReady = true; splitDirty = true; schedule(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();

